'use client';

import { VpButton, VpIcon } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useLayoutEffect, useState } from 'react';
import { Element } from 'react-scroll';
import { Item } from '@/app/components/Discussion/Item';
import { ItemPost } from '@/app/components/Discussion/ItemPost';
import { Loader } from '@/app/components/Loader';
import { useAuthenticatedContext, useCommentsContext } from '@/app/components/Provider';
import { isPaginationHasMorePage } from '@/app/utils/entities/collection';
import { isMainUser } from '~/utils/user/user';
import { scrollToElement } from '~/utils/utils';

export const Comments = ({ resultId }: { resultId: number }) => {
  const t = useTranslations('DISCUSSION');
  const { user } = useAuthenticatedContext();

  const searchParams = useSearchParams();

  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);
  const [opened, setOpened] = useState(false);

  const maxVisibleItems = 2;

  const focusedItem = Number.parseInt(searchParams.get('c'), 10);

  const {
    answers,
    deleteReaction,
    getAnswers,
    getComments,
    pagination,
    postComment,
    postReaction,
    processing,
  } = useCommentsContext();

  const hasMore = isPaginationHasMorePage(pagination);

  useLayoutEffect(() => {
    const anchor = `c-${focusedItem}`;
    const element = document.querySelector(`[name="${anchor}"]`);
    if (
      element
      && answers?.find(o => o.id === focusedItem)
      && !hasAutoScrolled
    ) {
      setHasAutoScrolled(true);
      setTimeout(() => {
        scrollToElement(anchor);
      }, 800);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers?.length]);

  const handleView = async (e) => {
    e.target.blur();

    if (hasMore && !opened) {
      await getAnswers(true);
    }

    setOpened(!opened);
  };

  return (
    <Loader loading={processing}>
      <section className="results__comments" data-testid="results__comments">
        {isMainUser(user) && (
          <section className="results__comment-post">
            <ItemPost
              postComment={postComment}
              processing={processing}
              id={`post-opinion-${resultId}`}
            />
          </section>
        )}
        {(!!answers?.length || isMainUser(user)) && (
          <Element name={`q-${resultId}`}>
            {answers?.length > 0
              ? (
                  <>
                    {answers.filter((_, i) => (opened ? true : i < maxVisibleItems))
                      .map(opinion => (
                        <Item
                          key={opinion.id}
                          item={opinion}
                          getComments={getComments}
                          postComment={postComment}
                          postReaction={postReaction}
                          deleteReaction={deleteReaction}
                          processing={processing}
                          entityType="comments"
                        />
                      ))}
                    {answers.length >= maxVisibleItems && hasMore && (
                      <div className="load-more">
                        {hasMore || !opened
                          ? (
                              <VpButton
                                data-testid="see-more"
                                size="small"
                                startSlot={<VpIcon name="chevron-down" />}
                                onClick={handleView}
                                disabled={processing}
                              >
                                {t('SEE_MORE_COMMENT')}
                              </VpButton>
                            )
                          : (
                              <VpButton
                                data-testid="see-less"
                                size="small"
                                startSlot={<VpIcon name="chevron-up" />}
                                variant="tertiary"
                                onClick={handleView}
                                disabled={processing}
                              >
                                {t('SEE_LESS_COMMENT')}
                              </VpButton>
                            )}
                      </div>
                    )}
                  </>
                )
              : (
                  <div className="no-result" data-testid="no-result">
                    {t('POST_IDEA')}
                  </div>
                )}
          </Element>
        )}
      </section>
    </Loader>
  );
};
